"use client";

import i18n from "@/lang/i18n";
import { formatDiamond } from '@/utils/formatDiamond';
import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import {
  Box,
  Typography,
  Dialog,
  ButtonBase,
  CircularProgress,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TagIcon from "@mui/icons-material/Tag";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Icon } from "@/components/icons";
import { TEMP_FG_ACCENT } from "@/constants/tempColors";
import { useQuery } from "@tanstack/react-query";
import type { TransitionProps } from "@mui/material/transitions";
import { useDarkColors } from "@/hooks/useDarkColors";
import { useAuthStore } from "@/stores/authStore";
import { useAppStore } from "@/stores/appStore";
import { useTranslator } from "@/lang";
import { useDiamondShortageModal } from "@/hooks/useDiamondShortageModal";
import { useLoginModalStore } from "@/stores/loginModalStore";
import {
  registerTopPriorityBackInterceptor,
  unregisterTopPriorityBackInterceptor,
} from "@/stores/navigationStackStore";
import { createUserFeed } from "@/api/userFeedApi";
import { getMatchedCharacters, getRecommendedCharacters } from "@/api/feedApi";
import { getAssetUrl } from "@/utils/assetUrl";
import { APP_MAX_WIDTH } from "@/components/PageContent/PageContent";
import { useKeyboardHeight } from "@/hooks/useKeyboardHeight";
import { SAFE_AREA_BOTTOM } from "@/contexts/SafeAreaContext";
import {
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_REGULAR_M,
  TEMP_TEXT_REGULAR_XS,
  TEMP_TEXT_BOLD_XS,
  TEMP_TEXT_BOLD_XXS,
  TEMP_TITLE_EXTRABOLD_XL,
} from "@/constants/tempTypo";
import { usePortalStore } from "@/stores/portalStore";
import type {
  MatchedCharacterDto,
  RandomCharacterItemDto,
} from "@/shared/types/api/Api";
import SignupRewardInfo from "@/components/SignupRewardInfo/SignupRewardInfo";

const MAX_LEN = 500;

type MentionableCharacter = MatchedCharacterDto & {
  /** 골드 체크 공식 뮤즈. 내 피드 멘션 대상에서는 임시 제외한다. */
  isRealPerson?: boolean;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onComposed: () => void;
  freePostsRemaining: number;
}

const SlideUp = forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function characterDisplayName(name: any): string {
  if (!name) return "";
  if (typeof name === "string") return name;
  if (typeof name === "object")
    return name[i18n.language] ?? name.ko ?? name.en ?? Object.values(name)[0] ?? "";
  return "";
}

export default function FeedComposerSheet({
  open,
  onClose,
  onComposed,
  freePostsRemaining,
}: Props) {
  const c = useDarkColors();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const keyboardHeight = useKeyboardHeight();
  const isAppEnv = useAppStore((s) => s.isInApp);
  const appPlatform = useAppStore((s) => s.appPlatform);
  // BottomNav 와 동일 정책: 인앱 iOS=20px / Android=6px / 웹=0 의 보정 패딩.
  const appExtraPadding = isAppEnv ? (appPlatform === "android" ? 6 : 20) : 0;
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const { t, f } = useTranslator();
  const { showDiamondShortage } = useDiamondShortageModal();
  const openLoginModal = useLoginModalStore((s) => s.open);
  const openConfirm = usePortalStore((s) => s.openConfirm);

  const showSignupModal = () => {
    openConfirm({
      title: t("profile.login_required_title"),
      subtitle: t("profile.signup_subtitle"),
      infoContent: <SignupRewardInfo />,
      cancelText: t("profile.cancel"),
      confirmText: t("profile.signup_cta"),
      confirmIcon: true,
      onConfirm: () => openLoginModal(),
    });
  };

  // 흔들림/스크롤 방지:
  //  (1) body 자체를 fixed 로 잠궈서 iOS textarea focus 시 자동 scroll-into-view 원천 차단
  //  (2) Dialog 컨테이너의 height 만 visualViewport.height 로 직접 박음 — top 은 건드리지 않음 (= 헤더 안 흔들림)
  //      RAF throttle 로 한 프레임당 1번만 변경. React state 안 거침.
  useEffect(() => {
    if (typeof window === "undefined" || !open) return;

    const html = document.documentElement;
    const body = document.body;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyWidth = body.style.width;
    const scrollYAtOpen = window.scrollY;

    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollYAtOpen}px`;
    body.style.width = "100%";

    // 컨테이너 height 를 visualViewport.height 로 직접 박기. top 은 0 고정 → 헤더 안 흔들림.
    let rafId: number | null = null;
    const apply = () => {
      const el = document.querySelector(
        ".MuiDialog-container",
      ) as HTMLElement | null;
      if (!el) return;
      const v = window.visualViewport;
      el.style.height = v ? `${v.height}px` : "100dvh";
    };
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        apply();
      });
    };
    // Dialog DOM 마운트 후 첫 적용
    schedule();
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollYAtOpen);
    };
  }, [open]);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const [content, setContent] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTips, setShowTips] = useState(true);

  // 멘션
  const [mentionedCharacterIds, setMentionedCharacterIds] = useState<string[]>(
    [],
  );
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [cursorPos, setCursorPos] = useState<number>(0);

  // 다이얼로그 열리면 textarea 자동 포커스 시도.
  // iOS는 user gesture가 아니면 키보드를 안 띄우지만, 데스크탑/안드로이드에선 키보드가 올라옴.
  // iOS에선 사용자가 textarea를 한 번 탭해서 키보드를 띄우게 됨 (정상 UX).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      textareaRef.current?.focus();
    }, 350);
    return () => clearTimeout(t);
  }, [open]);

  // 스와이프/하드웨어 백 가로채기 (navigationStackStore의 최우선 인터셉터 사용)
  // 백 제스처 → Dialog만 닫고 상위 스택(/my-feed)은 그대로 둠.
  // 빠른 두 번째 swipe도 페이지 이탈 차단 — 핸들러에서 새 guard push + interceptor 재무장.
  const isClosingRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;

    isClosingRef.current = false;
    const GUARD = "__feedComposerGuard";
    history.pushState({ [GUARD]: true }, "");

    const handler = () => {
      if (isClosingRef.current) return;
      isClosingRef.current = true;
      // 후속 swipe로 인한 페이지 이탈 방지
      history.pushState({ [GUARD]: true }, "");
      registerTopPriorityBackInterceptor(handler);
      onClose();
    };
    registerTopPriorityBackInterceptor(handler);

    return () => {
      unregisterTopPriorityBackInterceptor(handler);
      if (
        typeof window !== "undefined" &&
        (history.state as Record<string, unknown> | null)?.[GUARD] === true
      ) {
        // X/제출 닫기 — history.back() popstate를 소화할 일회용 인터셉터
        registerTopPriorityBackInterceptor(() => {});
        history.back();
      }
    };
  }, [open, onClose]);

  // 멘션 자동완성용 — 매칭된 캐릭터 + 추천(전체) 캐릭터 둘 다 받아서 합침
  // 매칭 안 됐어도 모두 보여줘서 사용자가 마음에 드는 친구를 부를 수 있게.
  const { data: matchedData } = useQuery({
    queryKey: ["matched-characters"],
    queryFn: getMatchedCharacters,
    enabled: open && isLoggedIn,
    staleTime: 60_000,
  });
  const { data: recommendedData } = useQuery({
    queryKey: ["recommended-characters-mention"],
    queryFn: () => getRecommendedCharacters({ count: 20 }),
    enabled: open, // 비로그인도 사용 가능
    staleTime: 60_000,
  });
  const characters: MentionableCharacter[] = useMemo(() => {
    const matched = (matchedData?.characters ?? []) as MentionableCharacter[];
    const seen = new Set(matched.map((m) => m.characterId));
    // 매칭 안 된 추천 캐릭터를 친밀도 0으로 추가
    const recommended = (recommendedData?.characters ?? []) as Array<
      RandomCharacterItemDto & { isRealPerson?: boolean }
    >;
    const extras: MentionableCharacter[] = recommended
      .filter((r) => !seen.has(r.characterId))
      .map((r) => ({
        characterId: r.characterId,
        name: (r.name ?? {}) as object,
        profileImagePath: r.profileImagePath ?? null,
        intimacy: 0,
        isGirlfriend: !!r.isGirlfriend,
        canUseIntimacyItem: false,
        isRealPerson: r.isRealPerson,
      }));
    return [...matched, ...extras].filter((ch) => ch.isRealPerson !== true);
  }, [matchedData, recommendedData]);
  const characterMap = useMemo(() => {
    const map = new Map<string, MentionableCharacter>();
    for (const ch of characters) map.set(ch.characterId, ch);
    return map;
  }, [characters]);

  // 백엔드 FEED_POST_DIAMOND_COST와 동기 (user-feed.service.ts)
  const cost = freePostsRemaining > 0 ? 0 : 20;
  const canSubmit = content.trim().length > 0 && !submitting;

  // 본문 파싱
  const hashtags = useMemo(() => parseHashtags(content), [content]);
  const mentionedNames = useMemo(() => parseMentionNames(content), [content]);

  // 본문에서 사라진 멘션 ID 정리
  useEffect(() => {
    if (mentionedCharacterIds.length === 0) return;
    const stillIn = mentionedCharacterIds.filter((id) => {
      const ch = characterMap.get(id);
      if (!ch) return false;
      const name = characterDisplayName(ch.name);
      return name && content.includes("@" + name);
    });
    if (stillIn.length !== mentionedCharacterIds.length) {
      setMentionedCharacterIds(stillIn);
    }
  }, [content, characterMap, mentionedCharacterIds]);

  const reset = () => {
    setContent("");
    setImage(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setError(null);
    setMentionQuery(null);
    setMentionedCharacterIds([]);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(t("my_feed.composer.error.image_only"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t("my_feed.composer.error.image_size"));
      return;
    }
    // 1장만 — 기존 이미지가 있으면 교체
    setImage(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // textarea auto-grow — 평상시는 무제한 성장 + textarea 내부 스크롤 OFF.
  // 스크롤은 항상 body 한 곳에서만 일어나야 위아래 진동(rubber-band 충돌) 안 생김.
  // 멘션 활성 시에만 cap을 둬서 드롭다운 공간 확보.
  const autoGrowTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    if (mentionQuery !== null) {
      ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
      ta.style.overflowY = "auto";
    } else {
      // 내용만큼 자라며 textarea 내부 스크롤 없음 — body가 단일 스크롤 컨텍스트
      ta.style.height = ta.scrollHeight + "px";
      ta.style.overflowY = "hidden";
    }
    // overlay 동기화
    if (highlightRef.current) {
      highlightRef.current.style.height = ta.style.height;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value.slice(0, MAX_LEN);
    setContent(val);
    const pos = e.target.selectionStart ?? val.length;
    setCursorPos(pos);
    setMentionQuery(extractMentionQuery(val, pos));
    // 다음 paint에 높이 재계산
    requestAnimationFrame(autoGrowTextarea);
  };

  // 다이얼로그 열릴 때 / 외부에서 content 바뀔 때 / 멘션 활성 토글 시 높이 동기화
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(autoGrowTextarea);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, content, mentionQuery]);

  const handleSelectMention = (ch: MentionableCharacter) => {
    if (ch.isRealPerson === true) return;
    const name = characterDisplayName(ch.name);
    if (!name) return;
    // 동기 focus — 키보드 유지/재오픈
    textareaRef.current?.focus();

    const before = content.slice(0, cursorPos);
    const after = content.slice(cursorPos);
    const atIdx = before.lastIndexOf("@");
    if (atIdx === -1) return;
    const replaced = before.slice(0, atIdx) + "@" + name + " " + after;
    setContent(replaced.slice(0, MAX_LEN));
    setMentionQuery(null);
    setMentionedCharacterIds((prev) =>
      prev.includes(ch.characterId) ? prev : [...prev, ch.characterId],
    );
    setTimeout(() => {
      const newPos = atIdx + 1 + name.length + 1;
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  };

  /**
   * 텍스트에어리어 현재 커서 위치 기준 스니펫 삽입.
   * - iOS Safari는 user gesture 핸들러 안에서 .focus()를 **동기**로 호출해야 키보드가 올라옴.
   *   setTimeout 안에서 호출하면 gesture 체인이 끊겨서 키보드가 안 뜸 → 동기로 먼저 focus.
   * - 호출 시점에 textarea의 selectionStart를 직접 읽어와서 stale cursorPos 회피
   * - 멘션(@)일 때는 자동완성 드롭다운 즉시 표시
   */
  const insertAtCursor = (snippet: string) => {
    const ta = textareaRef.current;
    // 1) 동기 focus — 키보드 띄움 (iOS gesture 요건)
    ta?.focus();

    // 2) 현재 커서 위치 (focus 직후 selectionStart는 보존됨)
    const pos = ta?.selectionStart ?? content.length;
    const before = content.slice(0, pos);
    const after = content.slice(pos);

    // 앞 글자가 공백/개행/시작 아니면 멘션·해시태그가 인식되지 않으므로 공백 prepend
    const needsLeadingSpace =
      (snippet === "@" || snippet === "#") &&
      before.length > 0 &&
      !/[\s\n]$/.test(before);
    const finalSnippet = (needsLeadingSpace ? " " : "") + snippet;

    const next = (before + finalSnippet + after).slice(0, MAX_LEN);
    setContent(next);
    const newPos = before.length + finalSnippet.length;
    setCursorPos(newPos);

    // 멘션 클릭이면 즉시 빈 query로 드롭다운 표시
    if (snippet === "@") {
      setMentionQuery("");
    }

    // 3) 다음 tick에 selection 복원 (state 반영 후)
    setTimeout(() => {
      const t = textareaRef.current;
      if (!t) return;
      t.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!isLoggedIn) {
      showSignupModal();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 본문 내 @이름과 일치하는 캐릭터 ID만 채택
      const finalMentionIds = mentionedCharacterIds.filter((id) => {
        const ch = characterMap.get(id);
        if (!ch) return false;
        const name = characterDisplayName(ch.name);
        return name && content.includes("@" + name);
      });

      await createUserFeed({
        content: content.trim(),
        image,
        mentionedCharacterIds: finalMentionIds,
      });
      reset();
      onComposed();
    } catch (err: any) {
      const code = err?.response?.data?.message ?? err?.message;
      if (code === "INSUFFICIENT_BALANCE") {
        showDiamondShortage();
      } else if (code === "USER_FEED_CONTENT_TOO_LONG") {
        setError(t("my_feed.composer.error.too_long"));
      } else {
        setError(t("my_feed.composer.error.create_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const suggestionList = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return characters
      .map((c) => ({ char: c, name: characterDisplayName(c.name) }))
      .filter(
        ({ name }) => name && (q === "" || name.toLowerCase().includes(q)),
      )
      .slice(0, 6);
  }, [mentionQuery, characters]);

  // 멘션 칩 데이터 (본문에 등장하는 @이름 → 캐릭터 매칭)
  const mentionChips = useMemo(() => {
    return mentionedNames
      .map((name) => {
        const ch = characters.find(
          (c) => characterDisplayName(c.name) === name,
        );
        return ch ? { name, ch } : null;
      })
      .filter((v): v is { name: string; ch: MentionableCharacter } => !!v);
  }, [mentionedNames, characters]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={false}
      TransitionComponent={SlideUp}
      // 닫힐 때는 exit 애니메이션을 사실상 제거.
      // 스와이프-백 시 visualViewport 변화(키보드 닫힘)와 slide-down이 겹쳐 "위로 튀었다 사라지는" 고스트가 생기는 걸 차단.
      transitionDuration={{ enter: 220, exit: 0 }}
      // 모바일: Dialog 컨테이너 자체를 visualViewport에 정확히 맞춤 → 키보드가 뜨면 컨테이너가 줄어들고
      // 그 안의 flex column(헤더+본문+액션바)이 자동으로 재배치됨 → 액션바는 항상 키보드 바로 위, 헤더는 항상 최상단.
      // 닫히는 순간엔 vv-기반 sx를 떼서 컨테이너가 점프하지 않게 한다.
      // 뒤의 페이지(My Feed 리스트) 가 키보드 올라오는 짧은 순간 backdrop 사이로 비치는 걸 차단.
      // backdrop 자체를 시트 배경색으로 채워 비침 자체를 없앤다.
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: c.bg,
          },
        },
      }}
      // 모바일: 100dvh (dynamic viewport) 로 OS 가 키보드 올라옴 / 내려감을 직접 처리.
      // JS 가 visualViewport 추적하면 한 프레임 lag 가 "위아래 흔들림" 으로 보이므로
      // 추적 자체를 빼고 OS에 위임. iOS 16+ / Android Chrome 108+ 지원.
      sx={
        isMobile && open
          ? {
              "& .MuiDialog-container": {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                width: "100%",
                height: "100dvh",
                overflow: "hidden",
              },
            }
          : undefined
      }
      PaperProps={{
        sx: {
          bgcolor: c.bg,
          width: isMobile ? "100%" : APP_MAX_WIDTH,
          maxWidth: isMobile ? "100%" : APP_MAX_WIDTH,
          height: isMobile ? "100%" : "90vh",
          maxHeight: isMobile ? "100%" : "90vh",
          m: 0,
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          // Paper는 컨테이너의 flex 중앙정렬 — 모바일에선 100%×100%로 fit
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: c.bg,
          position: "relative",
        }}
      >
        {/* 헤더 — 항상 상단 고정 */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${c.border}`,
            bgcolor: c.bg,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleClose}
            disabled={submitting}
            aria-label={t("my_feed.composer.aria.close")}
          >
            <CloseIcon sx={{ color: c.fg }} />
          </IconButton>
          <Typography sx={{ ...TEMP_TITLE_EXTRABOLD_XL, color: c.fg }}>
            {t("my_feed.composer.title")}
          </Typography>
          <ButtonBase
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              minWidth: 64,
              px: 2,
              py: 0.75,
              borderRadius: 999,
              background: canSubmit
                ? "linear-gradient(135deg, #ff6ec7 0%, #ff8a3d 100%)"
                : c.fgDisabled,
              color: "#fff",
              ...TEMP_TEXT_BOLD_M,
              opacity: canSubmit ? 1 : 0.6,
              boxShadow: canSubmit
                ? "0 2px 8px rgba(255,110,199,0.35)"
                : "none",
            }}
          >
            {submitting ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : cost === 0 ? (
              t("my_feed.composer.post")
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {t("my_feed.composer.post")} ·
                <Icon name="icDiamondFilled" size={16} />
                {formatDiamond(cost)}
              </Box>
            )}
          </ButtonBase>
        </Box>

        {/* 비용 안내 */}
        <Box
          sx={
            cost === 0
              ? {
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderBottom: `1px solid ${c.border}`,
                  bgcolor: c.bgInset,
                  flexShrink: 0,
                }
              : {}
          }
        >
          {cost === 0 ? (
            <>
              <Chip
                label={t("my_feed.composer.free_chip")}
                size="small"
                sx={{ bgcolor: "#ff6ec7", color: "#fff", fontWeight: 700 }}
              />
              <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLow }}>
                {f(t("my_feed.composer.free_count"), [freePostsRemaining])}
              </Typography>
            </>
          ) : (
            <></>
          )}
        </Box>

        {/* 본문 영역 — 헤더와 액션바 사이만 스크롤됨 (Dialog 컨테이너가 viewport에 fit되므로 추가 offset 불필요) */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain", // 스크롤이 부모로 전파되지 않음 — 진동 방지
            px: 2,
            pt: 2,
            pb: 2,
            position: "relative",
            minHeight: 0,
          }}
        >
          {/* 팁 박스 — 멘션 활성 시 자동 숨김으로 공간 확보 */}
          {showTips && mentionQuery === null && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${c.border}`,
                bgcolor: c.bgInset,
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <HelpOutlineIcon
                sx={{ fontSize: 18, color: c.primary, mt: "2px" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fg, mb: 0.5 }}>
                  {t("my_feed.composer.tip.title")}
                </Typography>
                <Typography
                  sx={{
                    ...TEMP_TEXT_REGULAR_XS,
                    color: c.fgLow,
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                  }}
                >
                  {t("my_feed.composer.tip.body")}
                </Typography>
              </Box>
              <IconButton
                size="small"
                // textarea blur 방지 — 클릭 시 키보드 안 내려감
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => {
                  try {
                    e.preventDefault();
                  } catch {}
                }}
                onClick={() => setShowTips(false)}
                sx={{ color: c.fgLow }}
                aria-label={t("my_feed.composer.tip.aria.close")}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {/* textarea — 뒤에 하이라이트 레이어를 깔아 #/@ 부분을 색칠해서 보여준다.
              textarea는 텍스트 투명 + 커서만 표시 → 사용자는 색칠된 글자 위에서 타이핑하는 느낌. */}
          <Box sx={{ position: "relative" }}>
            {/* 하이라이트 미러 — textarea와 동일한 폰트/사이즈/패딩 */}
            <Box
              ref={highlightRef}
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                // textarea 내부 스크롤이 활성일 때만 overlay 스크롤 — 평상시는 hidden (body가 스크롤)
                overflow: "hidden",
                color: c.fg,
                fontSize: "16px",
                lineHeight: 1.6,
                fontFamily: "inherit",
                padding: 0,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {renderHighlightedText(content, c)}
              {/* 줄 끝에 newline이 있을 때 height가 맞도록 zero-width 추가 */}
              {content.endsWith("\n") && "​"}
            </Box>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleChange}
              onScroll={(e) => {
                // textarea 스크롤과 하이라이트 레이어 동기화
                const t = e.target as HTMLTextAreaElement;
                if (highlightRef.current) {
                  highlightRef.current.scrollTop = t.scrollTop;
                }
              }}
              onSelect={(e) => {
                const pos =
                  (e.target as HTMLTextAreaElement).selectionStart ?? 0;
                setCursorPos(pos);
                setMentionQuery(extractMentionQuery(content, pos));
              }}
              placeholder={t("my_feed.composer.placeholder")}
              maxLength={MAX_LEN}
              rows={3}
              style={{
                position: "relative",
                width: "100%",
                minHeight: 72,
                resize: "none",
                border: "none",
                outline: "none",
                background: "transparent",
                // 텍스트 자체는 투명, 커서만 보이게 → 뒤의 하이라이트 레이어 텍스트가 보임
                color: "transparent",
                caretColor: c.fg,
                // iOS Safari에서 선택 영역 색상은 유지
                WebkitTextFillColor: "transparent",
                fontSize: "16px",
                lineHeight: 1.6,
                fontFamily: "inherit",
                padding: 0,
                // overflowY는 autoGrowTextarea에서 동적 제어 (평상시 hidden, 멘션 중 auto)
                overflowY: "hidden",
                overscrollBehavior: "contain",
              }}
            />
          </Box>

          {/* 멘션 자동완성 — @ 입력 시 항상 노출 (목록 없어도 안내 표시) */}
          {mentionQuery !== null && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                bgcolor: c.bgCard,
                border: `1px solid ${c.border}`,
                borderRadius: 2,
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderBottom: `1px solid ${c.border}`,
                  bgcolor: c.bgInset,
                }}
              >
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
                  {t("my_feed.composer.mention.header")}
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 180, overflowY: "auto" }}>
                {suggestionList.length === 0 && (
                  <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                    <Typography
                      sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLower }}
                    >
                      {characters.length === 0
                        ? t("my_feed.composer.mention.loading")
                        : t("my_feed.composer.mention.empty")}
                    </Typography>
                  </Box>
                )}
                {suggestionList.map(({ char, name }) => (
                  <ButtonBase
                    key={char.characterId}
                    onMouseDown={(e) => e.preventDefault()}
                    onTouchStart={(e) => {
                      try {
                        e.preventDefault();
                      } catch {}
                    }}
                    onClick={() => handleSelectMention(char)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      px: 1.5,
                      py: 0.75,
                      gap: 1,
                      justifyContent: "flex-start",
                      "&:hover": { bgcolor: c.bgInset },
                      borderBottom: `1px solid ${c.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: c.bgInset,
                        backgroundImage: char.profileImagePath
                          ? `url(${getAssetUrl(char.profileImagePath, "thumb")})`
                          : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                    <Box sx={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fg }}>
                        {name}
                      </Typography>
                    </Box>
                    {/* 친밀도 뱃지 — 채팅 리스트와 동일 컴포넌트 */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: "2px",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: c.bgInset,
                        borderRadius: "9999px",
                        pl: "6px",
                        pr: "8px",
                        py: "4px",
                        flexShrink: 0,
                      }}
                    >
                      <Icon
                        name="icHeartFilled"
                        size={10}
                        color={TEMP_FG_ACCENT}
                      />
                      <Typography
                        sx={{
                          ...TEMP_TEXT_BOLD_XXS,
                          color: c.fg,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {char.intimacy}°C
                        {(!char.isGirlfriend && char.intimacy >= 89) ||
                        char.intimacy >= 100
                          ? " MAX"
                          : ""}
                      </Typography>
                    </Box>
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          )}

          {/* 태그/멘션 칩 (아바타 포함) */}
          {(hashtags.length > 0 || mentionChips.length > 0) && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignItems: "center",
              }}
            >
              {hashtags.map((tag) => (
                <Chip
                  key={`tag-${tag}`}
                  label={`#${tag}`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(75, 0, 204, 0.08)",
                    color: c.primary,
                    fontWeight: 600,
                  }}
                />
              ))}
              {mentionChips.map(({ name, ch }) => (
                <Chip
                  key={`men-${ch.characterId}`}
                  avatar={
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundImage: ch.profileImagePath
                          ? `url(${getAssetUrl(ch.profileImagePath, "thumb")})`
                          : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        bgcolor: c.bgInset,
                      }}
                    />
                  }
                  label={`@${name}`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255, 110, 199, 0.15)",
                    color: "#d63aa3",
                    fontWeight: 700,
                    pl: 0.25,
                  }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
            <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLower }}>
              {content.length} / {MAX_LEN}
            </Typography>
          </Box>

          {/* 이미지 프리뷰 */}
          {previewUrl && (
            <Box
              sx={{
                mt: 2,
                position: "relative",
                borderRadius: 2,
                overflow: "hidden",
                maxHeight: 360,
              }}
            >
              <img
                src={previewUrl}
                alt=""
                style={{
                  display: "block",
                  width: "100%",
                  maxHeight: 360,
                  objectFit: "cover",
                }}
              />
              <IconButton
                onClick={() => {
                  setImage(null);
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {error && (
            <Typography
              sx={{ ...TEMP_TEXT_REGULAR_M, color: "#e53935", mt: 2 }}
            >
              {error}
            </Typography>
          )}
        </Box>

        {/* 하단 액션 바 — Dialog 컨테이너가 viewport에 맞춰져 있으므로 normal flow의 bottom = 키보드 바로 위.
            키보드가 없을 때 (인앱 iOS) 홈 인디케이터(safe-area-bottom) 와 액션바가 겹치는 이슈가 있어
            키보드 닫혀 있을 때만 safe-area 만큼 추가 패딩을 준다. 키보드 떴을 때는 0 으로 (이미 키보드 위에 붙어있음). */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${c.border}`,
            px: 2,
            pt: 1,
            pb:
              keyboardHeight > 0
                ? 1
                : `calc(${8 + appExtraPadding}px + ${SAFE_AREA_BOTTOM})`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: c.bg,
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <QuickAction
            label={
              image
                ? f(t("my_feed.composer.action.photo_with_count"), [1])
                : t("my_feed.composer.action.photo")
            }
            icon={<ImageOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => fileRef.current?.click()}
            preserveFocus={false}
          />
          <QuickAction
            label={t("my_feed.composer.action.tag")}
            icon={<TagIcon sx={{ fontSize: 18 }} />}
            onClick={() => insertAtCursor("#")}
          />
          <QuickAction
            label={t("my_feed.composer.action.mention")}
            icon={<AlternateEmailIcon sx={{ fontSize: 18 }} />}
            onClick={() => insertAtCursor("@")}
            highlight
          />
        </Box>
      </Box>
    </Dialog>
  );
}

function QuickAction({
  label,
  icon,
  onClick,
  highlight = false,
  preserveFocus = true,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  highlight?: boolean;
  /** true면 mousedown 단계에서 preventDefault — textarea blur 방지 (키보드 안 닫힘) */
  preserveFocus?: boolean;
}) {
  const c = useDarkColors();
  return (
    <ButtonBase
      onMouseDown={(e) => {
        if (preserveFocus) e.preventDefault();
      }}
      onTouchStart={(e) => {
        if (preserveFocus) {
          // 모바일에서 textarea focus 유지 — passive 옵션 없이 preventDefault 가능
          // (preserveFocus=true는 click 직전에 blur가 발생하는 것을 막는다)
          // touch 이벤트에서 preventDefault해도 click은 정상 발생
          // 단, scroll/pinch는 막히지 않게 — ButtonBase 내부라 안전
          try {
            e.preventDefault();
          } catch {}
        }
      }}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.25,
        py: 0.75,
        borderRadius: 999,
        bgcolor: highlight ? "rgba(255, 110, 199, 0.12)" : c.bgInset,
        color: highlight ? "#d63aa3" : c.fg,
      }}
    >
      {icon}
      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: "inherit" }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

// ─── helpers ───────────────────────────────────────────────────

function parseHashtags(content: string): string[] {
  const re = /#([\p{L}\p{N}_]{1,20})/gu;
  const set = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    set.add(m[1]);
    if (set.size >= 10) break;
  }
  return Array.from(set);
}

function parseMentionNames(content: string): string[] {
  const re = /@([\p{L}\p{N}_]{1,20})/gu;
  const set = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    set.add(m[1]);
    if (set.size >= 5) break;
  }
  return Array.from(set);
}

function extractMentionQuery(content: string, cursor: number): string | null {
  const upto = content.slice(0, cursor);
  const atIdx = upto.lastIndexOf("@");
  if (atIdx === -1) return null;
  if (atIdx > 0) {
    const prev = upto[atIdx - 1];
    if (!/[\s\n]/.test(prev)) return null;
  }
  const tail = upto.slice(atIdx + 1);
  if (/\s/.test(tail)) return null;
  if (tail.length > 20) return null;
  return tail;
}

// 본문 텍스트를 받아 #해시태그/@멘션 부분을 색칠된 span으로 감싼 React 노드 배열을 반환.
// textarea 뒤의 하이라이트 레이어에서 사용 — 폰트/사이즈는 textarea와 동일해야 정렬됨.
// 굵기/배경은 사용하지 않음 (글자 폭이 달라지면 textarea와 어긋남).
function renderHighlightedText(
  text: string,
  c: { primary: string },
): React.ReactNode[] {
  if (!text) return [];
  const re = /(#[\p{L}\p{N}_]{1,20}|@[\p{L}\p{N}_]{1,20})/gu;
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    const isHash = token.startsWith("#");
    out.push(
      <span
        key={`h-${key++}`}
        style={{
          color: isHash ? c.primary : "#d63aa3",
        }}
      >
        {token}
      </span>,
    );
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
